import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * DefinePage Component
 * 
 * Displays define tools content like the legacy HTML site
 */
const DefinePage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Define intro content from the legacy HTML
  const defineIntroHtml = `
    <p>
    Before moving in to ideation phase, we can take the time to properly define exactly what it is that we are aiming to create.     
    </p>

    <p>
    At this stage we can use a number of tools to help with this definition, setting us for ideation by clearly laying out what we want to achieve. 
    </p>

    <p>
    Everything is dependent on the context, but these tools should cover most bases for moving forwards - they will cover requirements for both ideation and the development team. 
    </p>
  `;

  // Templates HTML for each tool
  const fiveWsTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/define/5-ws.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koWzAkU=/" className="link-table">Miro</a>
    </>
  );

  const scenariosTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/define/scenarios.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koV_HT0=/" className="link-table">Miro</a>
    </>
  );

  const jobStoryTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/define/job-story.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koV_4Tk=/" className="link-table">Miro</a>
    </>
  );

  const userStoryTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/define/user-story.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koV2eIA=/" className="link-table">Miro</a>
    </>
  );

  const howMightWeTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/synthesis/define/how-might-we.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_koWIbS8=/" className="link-table">Miro</a>
    </>
  );

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {/* Define Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/synthesis">Synthesis</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/synthesis/define">Define</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={defineIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#5-ws">5 Ws</Link></div>
            <div><Link className="tool-section-cta" to="#scenarios">Scenarios</Link></div>
            <div><Link className="tool-section-cta" to="#job-story">Job Story</Link></div>
            <div><Link className="tool-section-cta" to="#user-story">User Story</Link></div>
            <div><Link className="tool-section-cta" to="#how-might-we">How Might We</Link></div>
          </div>
        </div>
      </div>

      {/* 5 Ws Section */}
      <main className="case-study">
        <span className="case-tool-title" id="5-ws">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="5-ws">
            <span className="tool-title-number">
              <Link to="#5-ws">5 Ws</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/define">Define</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#5-ws"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-5-ws" 
            id="tab-5-ws-overview" 
            defaultChecked
          />
          <label htmlFor="tab-5-ws-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Define", "20/09/20", fiveWsTemplates, <a href="https://en.wikipedia.org/wiki/Five_Ws">Aristotle</a>)}
              <div className="tool-opening">
                <p>
                The 5Ws(+H) technique can be used in 2 modes, either by listing out the factors involved in the problem (as seen in the Problem Log), or by listing out the factors for the solution.
                </p>
            
                <p>
                As we've previously identified, validated and synthised the problem, and are moving towards the solution we can use the 5Ws to clarify the progress we've made so far, and confirm exactly what we're hoping to achieve in the future stages. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://medium.com/ux-design-research/the-5ws-1-h-for-building-a-better-product-from-the-start-d1d33f9766a0" className="link-table">The 5Ws (& 1 H!) for Building a Better Product from the Start</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.infragistics.com/community/blogs/b/ux/posts/5w-h-knowledge-to-design-an-excellent-user-experience" className="link-table">5W+H = Knowledge to Design an Excellent User Experience</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://52weeksofux.com/post/890288783/the-five-ws-of-ux" className="link-table">The Five W's of UX</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-5-ws" 
            id="tab-5-ws-template"
          />
          <label htmlFor="tab-5-ws-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_koWzAkU=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="5 Ws Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Scenarios Section */}
      <main className="case-study">
        <span className="case-tool-title" id="scenarios">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="scenarios">
            <span className="tool-title-number">
              <Link to="#scenarios">Scenarios</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/define">Define</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#scenarios"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-scenarios" 
            id="tab-scenarios-overview" 
            defaultChecked
          />
          <label htmlFor="tab-scenarios-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Define", "20/09/20", scenariosTemplates, <a href="#">N/A</a>)}
              <div className="tool-opening">
                <p>
                Scenarios are another tool that helps set us up for the ideation phase by linking goal-driven interactions between the user/persona and the product. These 'mini-narratives' or stories can inspire the design and whilst keeping focus on the user goal. 
                </p>
            
                <p>
                We can then use the scenario with other tools to help craft the narrative to line us up for the solution. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/topics/user-scenarios" className="link-table">What are User Scenarios?</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxknowledgebase.com/scenarios-43e05671b07" className="link-table">Scenarios</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.usability.gov/how-to-and-tools/methods/scenarios.html" className="link-table">Scenarios</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-scenarios" 
            id="tab-scenarios-template"
          />
          <label htmlFor="tab-scenarios-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kobIlwM=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Scenarios Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Job Story Section */}
      <main className="case-study">
        <span className="case-tool-title" id="job-story">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="job-story">
            <span className="tool-title-number">
              <Link to="#job-story">Job Story</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/define">Define</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#job-story"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-job-story" 
            id="tab-job-story-overview" 
            defaultChecked
          />
          <label htmlFor="tab-job-story-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Define", "20/09/20", jobStoryTemplates, <a href="https://www.intercom.com/blog/accidentally-invented-job-stories/">Intercom</a>)}
              <div className="tool-opening">
                <p>
                The output of the Jobs-to-be-done theory, a Job Story succinctly summarises what we believe needs to be created by defining the solution as a 'Job'. 
                </p>
            
                <p>
                By understanding the situation the user is in, what they need to do and ultimately the goal they want to accomplish, a Job Story sets us up for the ideation phase and helps keep any solution pinned to the exact 'job' a user wants to do.  
                </p>
            
                <p>
                We can then go on to create 'Related Jobs' that help us to further solve the user goal as part of the entire Job.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.intercom.com/blog/accidentally-invented-job-stories/" className="link-table">How we accidentally invented Job Stories</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.intercom.com/blog/using-job-stories-design-features-ui-ux/" className="link-table">Designing features using Job Stories</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://jtbd.info/5-tips-for-writing-a-job-story-7c9092911fc9" className="link-table">5 Tips For Writing A Job Story</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="http://www.pivotbot.com/job-stories" className="link-table">Job Stories</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-job-story" 
            id="tab-job-story-template"
          />
          <label htmlFor="tab-job-story-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kobcwks=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Job Story Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* User Story Section */}
      <main className="case-study">
        <span className="case-tool-title" id="user-story">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="user-story">
            <span className="tool-title-number">
              <Link to="#user-story">User Story</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/define">Define</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#user-story"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-user-story" 
            id="tab-user-story-overview" 
            defaultChecked
          />
          <label htmlFor="tab-user-story-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Define", "20/09/20", userStoryTemplates, <a href="http://www.creativeeducationfoundation.org/facilitation_tool/5-ws-h/">Sakichi Toyoda</a>)}
              <div className="tool-opening">
                <p>
                Focusing on a specific user and their issue, a User Story is an agile method of depicting a user need or requirement in a specific format.
                </p>
            
                <p>
                A User Story can be written by anyone, but will generally be created by the Product Owner or Manager who will also manage them. They can be big and can be broken down in to smaller stories and then added to the Product Backlog.  
                </p>
            
                <p>
                As the Backlog is generally comprised of the Product Roadmap and Requirements, the User Stories will help tie user and business objectives together in a neat format to keep projects and progress efficient and on track.
                </p>
            
                <p>
                Along with the User Story, it is also helpful to include extra detail in the form of 'acceptance criteria', i.e. what needs to be done to accomplish the goal. This detailed scope will help the development team further understand the value, and set expectations for what would be considered 'done'. 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.mountaingoatsoftware.com/agile/user-stories" className="link-table">User Stories</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.atlassian.com/agile/project-management/user-stories" className="link-table">User Stories with Examples and Template</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://blog.easyagile.com/how-to-write-good-user-stories-in-agile-software-development-d4b25356b604" className="link-table">How to Write Good User Stories in Agile Software Development</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.visual-paradigm.com/guide/agile-software-development/what-is-user-story/" className="link-table">What is User Story?</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-user-story" 
            id="tab-user-story-template"
          />
          <label htmlFor="tab-user-story-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kmSDS0Q=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="User Story Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* How Might We Section */}
      <main className="case-study">
        <span className="case-tool-title" id="how-might-we">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="how-might-we">
            <span className="tool-title-number">
              <Link to="#how-might-we">How Might We</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/synthesis">Synthesis</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/synthesis/define">Define</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#how-might-we"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-how-might-we" 
            id="tab-how-might-we-overview" 
            defaultChecked
          />
          <label htmlFor="tab-how-might-we-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Define", "20/09/20", howMightWeTemplates, <a href="https://hbr.org/2012/09/the-secret-phrase-top-innovato">Min Basadur</a>)}
              <div className="tool-opening">
                <p>
                The phrase 'How Might We' (HMW) is full of optimism - it helps switch our mindset towards solving the problems, and opens up endless possibilities with the perception that the problem is solvable. It encourages innovative thinking whilst removing all constraints. 
                </p>
            
                <p>
                We can start the HMWs by talking about the problem, the previously discovered insights, and other definitions and then framing them in a way that creates multiple HMWs that are broad enough to solve the problem, but not too broad that makes any proposed solution impractical.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.designkit.org/methods/3" className="link-table">How Might We</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://beingagile.co.uk/sailboat-retrospective-step-by-step-guide-workshop-video-and-examples-gallery-being-agile/" className="link-table">"How Might We …?" Questions From Insights and User Stories</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-how-might-we" 
            id="tab-how-might-we-template"
          />
          <label htmlFor="tab-how-might-we-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_km2_d70=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="How Might We Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default DefinePage; 